import {
  Box,
  Chip,
  FormControl,
  InputAdornment,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useState } from "react";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";

const StyledChip = styled(Chip)(({ theme }) => ({
  margin: "2px 4px 2px 0px",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  "& .MuiChip-deleteIcon": {
    color: theme.palette.primary.contrastText,
    "&:hover": {
      color: theme.palette.primary.contrastText,
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    paddingRight: 8,
    minHeight: 56,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    alignContent: "center",
    "&.Mui-disabled": {
      minHeight: 48,
      padding: "8px 12px",
      backgroundColor: "transparent",
      "& .MuiInputBase-input": {
        display: "none",
      },
    },
  },
  "& .MuiInputBase-input": {
    padding: "8px 0",
    minWidth: "120px",
    flexGrow: 1,
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: theme.palette.divider,
    },
  },
  "& .MuiInputLabel-root": {
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
  },
  "& .MuiInputAdornment-root": {
    margin: "0",
    height: "auto",
    maxWidth: "100%",
  },
}));

const ChipsContainer = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "4px",
  alignItems: "center",
  margin: "4px 0",
});

interface EmailArrayFieldProps {
  name: string;
  label: string;
  disabled?: boolean;
  hideErrors?: boolean;
}

export const EmailArrayField = ({
  name,
  label,
  disabled,
  hideErrors,
}: EmailArrayFieldProps) => {
  const { control, setValue } = useFormContext();
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleKeyPress = (e: React.KeyboardEvent, currentEmails: string[]) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail(currentEmails);
    }
  };

  const handleAddEmail = (currentEmails: string[]) => {
    if (!inputValue.trim()) return;

    if (!validateEmail(inputValue)) {
      setError("Please enter a valid email address");
      return;
    }

    if (currentEmails.includes(inputValue)) {
      setError("This email is already added");
      return;
    }

    setValue(name, [...currentEmails, inputValue], {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    setInputValue("");
    setError(null);
  };

  const handleRemoveEmail = (
    emailToRemove: string,
    currentEmails: string[]
  ) => {
    setValue(
      name,
      currentEmails.filter((email) => email !== emailToRemove),
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      }
    );
  };

  return (
    <FormControl fullWidth>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error: formError } }) => (
          <Box>
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
              <StyledTextField
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, field.value || [])}
                onBlur={() => handleAddEmail(field.value || [])}
                label={label}
                placeholder={disabled ? "" : "type to add another"}
                disabled={disabled}
                error={!!error || !!formError}
                fullWidth
                variant="outlined"
                size="medium"
                multiline
                minRows={1}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ChipsContainer>
                        {(field.value || []).map((email: string) => (
                          <StyledChip
                            key={email}
                            label={email}
                            disabled={disabled}
                            onDelete={() =>
                              !disabled &&
                              handleRemoveEmail(email, field.value || [])
                            }
                            size="small"
                            variant="filled"
                          />
                        ))}
                      </ChipsContainer>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {error && (
              <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                {error}
              </Typography>
            )}
            <ErrorDisplay error={formError} hideErrors={hideErrors} />
          </Box>
        )}
      />
    </FormControl>
  );
};
